import { useState } from 'react';
import vignobleImg from '@/assets/terroir-vignoble.jpg';

type Terroir = {
  code: string;
  name: string;
  tag: string;
  count: number;
  km: string;
  dplus: string;
  accent: string;
  img: string;
  hero: string;
  detail: string;
};

const TERROIRS: Terroir[] = [
  {
    code: '01',
    name: 'LITTORAL',
    tag: 'SABLE · FALAISE · VENT',
    count: 412,
    km: '5—52',
    dplus: '0—400m',
    accent: '#00e5ff',
    img: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&auto=format&fit=crop',
    hero: "Marathon de la Côte d'Opale",
    detail: "De la digue de Deauville aux GR de Bretagne. L'air salé, le sable compacté, les falaises qui te coupent le souffle à la 27ᵉ borne.",
  },
  {
    code: '02',
    name: 'FORÊT',
    tag: 'SINGLE-TRACK · TECHNIQUE',
    count: 687,
    km: '10—80',
    dplus: '200—2 200m',
    accent: '#caff00',
    img: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&auto=format&fit=crop',
    hero: 'Trail de Fontainebleau',
    detail: "Fougères jusqu'aux genoux, racines traîtresses, lumière filtrée. Le terrain de jeu historique du trail français.",
  },
  {
    code: '03',
    name: 'MONTAGNE',
    tag: 'HAUTE-ALTITUDE · ULTRA',
    count: 198,
    km: '20—170',
    dplus: '1 500—10 000m',
    accent: '#ff2d9e',
    img: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=1200&auto=format&fit=crop',
    hero: 'UTMB · Chamonix',
    detail: "Là où le souffle manque et où la nuit tombe deux fois. Dénivelés qui te rappellent à l'ordre, ciels étoilés qui te sauvent.",
  },
  {
    code: '04',
    name: 'VILLE',
    tag: 'BITUME · TEMPO · FOULE',
    count: 743,
    km: '5—42',
    dplus: '0—150m',
    accent: '#f8ff3a',
    img: 'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=1200&auto=format&fit=crop',
    hero: 'Marathon de Paris',
    detail: "Pavés qui résonnent, haies humaines qui hurlent, un ravito tous les 5 km. La course la plus sociale qui existe.",
  },
  {
    code: '05',
    name: 'VIGNOBLE',
    tag: 'COTEAU · ROSÉ · SOLEIL',
    count: 156,
    km: '8—21',
    dplus: '100—600m',
    accent: '#c9a961',
    img: vignobleImg,
    hero: 'Marathon du Médoc',
    detail: "On court déguisé, on boit un verre tous les kilomètres, et on finit allongé dans un chai. Du sport oui, mais à la française.",
  },
  {
    code: '06',
    name: 'VOLCAN',
    tag: 'POUZZOLANE · RELIEF · RARE',
    count: 49,
    km: '15—85',
    dplus: '800—3 800m',
    accent: '#ff6a2c',
    img: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1200&auto=format&fit=crop',
    hero: 'Volcanic Experience',
    detail: "Auvergne, Réunion, Éoliennes. Terrain qui roule sous la chaussure, paysages lunaires, orgueil de finisher qui reste 10 ans.",
  },
];

const STATS = [
  { k: '2 245', l: 'courses référencées' },
  { k: '13', l: 'régions couvertes' },
  { k: '96', l: 'départements actifs' },
  { k: '4 810m', l: 'd+ max (UTMB)' },
];

const TerroirsSection = () => {
  const [active, setActive] = useState(0);

  return (
    <section
      className="relative border-t border-white/10 py-24 md:py-32"
      style={{
        background:
          'radial-gradient(ellipse at 20% 20%, rgba(0,229,255,0.04), transparent 60%), radial-gradient(ellipse at 90% 80%, rgba(255,45,158,0.04), transparent 50%)',
      }}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-8 mb-12">
          <div className="max-w-[680px]">
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-accent mb-4">
              // CHAPITRE TROIS · TERROIRS
            </div>
            <h2 className="m-0 leading-[0.9] tracking-tight">
              <span className="font-display italic uppercase -skew-x-6 inline-block text-[48px] md:text-[68px] lg:text-[96px] text-foreground">
                SIX DÉCORS.
              </span>
              <br />
              <span
                className="font-display italic uppercase -skew-x-6 inline-block text-[48px] md:text-[68px] lg:text-[96px] text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(90deg, #caff00 0%, #f8ff3a 35%, #ff7a1a 65%, #ff2d9e 100%)' }}
              >
                UNE COURSE
              </span>{' '}
              <span className="font-display italic uppercase -skew-x-6 inline-block text-[48px] md:text-[68px] lg:text-[96px] text-foreground">
                POUR CHACUN.
              </span>
            </h2>
          </div>
          <p className="max-w-[380px] text-sm leading-relaxed text-muted-foreground">
            La France a une topographie de dingue. Plutôt qu'une carte, on t'a découpé le pays en{' '}
            <b className="text-foreground">six terroirs de course</b> — du bitume parisien au volcan auvergnat, chacun avec ses codes, sa saison, son souffle.
          </p>
        </div>

        <div className="kourse-terroir-grid hidden md:grid">
          {TERROIRS.map((tr, i) => {
            const isActive = i === active;
            return (
              <div
                key={tr.code}
                className={`kourse-terroir ${isActive ? 'is-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                tabIndex={0}
                style={{
                  ['--accent' as any]: tr.accent,
                  backgroundImage: `linear-gradient(180deg, rgba(10,10,12,0.15) 0%, rgba(10,10,12,0.45) 55%, rgba(10,10,12,0.94) 100%), url(${tr.img})`,
                }}
              >
                <div className="kourse-terroir__code font-mono">{tr.code}</div>
                <div className="kourse-terroir__body">
                  <div className="kourse-terroir__tag font-mono">{tr.tag}</div>
                  <div className="kourse-terroir__name font-display italic">{tr.name}</div>
                  <div className="kourse-terroir__count">
                    <span style={{ color: tr.accent, fontWeight: 700 }}>{tr.count}</span>
                    <span className="ml-1.5 text-[10px] uppercase tracking-[0.14em] text-white/40">courses</span>
                  </div>
                </div>
                <div className="kourse-terroir__expand">
                  <div className="kourse-terroir__hero font-mono" style={{ color: tr.accent }}>▸ {tr.hero}</div>
                  <div className="kourse-terroir__detail">{tr.detail}</div>
                  <div className="kourse-terroir__specs">
                    <div>
                      <div className="kourse-terroir__spec-l font-mono">DIST</div>
                      <div className="kourse-terroir__spec-v font-display italic">
                        {tr.km}
                        <span className="text-[11px] text-white/50"> km</span>
                      </div>
                    </div>
                    <div>
                      <div className="kourse-terroir__spec-l font-mono">D+</div>
                      <div className="kourse-terroir__spec-v font-display italic">{tr.dplus}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="md:hidden flex flex-col gap-3">
          {TERROIRS.map((tr, i) => {
            const isOpen = i === active;
            return (
              <div
                key={tr.code}
                className={`kourse-terroir-mobile ${isOpen ? 'is-open' : ''}`}
                onClick={() => setActive(isOpen ? -1 : i)}
                style={{
                  ['--accent' as any]: tr.accent,
                  backgroundImage: `linear-gradient(180deg, rgba(10,10,12,0.2) 0%, rgba(10,10,12,0.55) 60%, rgba(10,10,12,0.95) 100%), url(${tr.img})`,
                }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-[11px] tracking-[0.14em] text-white/55">{tr.code}</span>
                    <span className="font-display italic text-2xl uppercase text-white">{tr.name}</span>
                  </div>
                  <span className="font-display italic" style={{ color: tr.accent }}>
                    {tr.count}
                  </span>
                </div>
                <div className="font-mono text-[10px] tracking-[0.14em] text-white/50 mt-1.5">{tr.tag}</div>
                {isOpen && (
                  <div className="mt-4 pt-4 border-t border-white/15 space-y-3">
                    <div className="font-mono text-[11px] tracking-[0.14em]" style={{ color: tr.accent }}>
                      ▸ {tr.hero}
                    </div>
                    <p className="text-sm text-white/80 leading-relaxed">{tr.detail}</p>
                    <div className="flex gap-6 pt-3">
                      <div>
                        <div className="font-mono text-[9px] tracking-[0.18em] text-white/48">DIST</div>
                        <div className="font-display italic text-lg text-white">
                          {tr.km} <span className="text-[11px] text-white/50">km</span>
                        </div>
                      </div>
                      <div>
                        <div className="font-mono text-[9px] tracking-[0.18em] text-white/48">D+</div>
                        <div className="font-display italic text-lg text-white">{tr.dplus}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-14 pt-7 pb-1 border-t border-b border-white/10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {STATS.map((s) => (
            <div key={s.l} className="flex flex-col gap-1">
              <div className="font-display italic -skew-x-6 text-[32px] md:text-[44px] lg:text-[56px] leading-none text-foreground">
                {s.k}
              </div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TerroirsSection;
